import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, ArrowRight, Check, Copy, KeyRound, LockKeyhole, RefreshCw, Server, ShieldAlert, Upload } from "lucide-react";
import { PlatformNav } from "./PlatformNav.js";
import { reauthenticateWithAccess } from "./step-up.js";
import {
  keyStatusPill,
  needsReauthentication,
  pairCheckView,
  platformResponse,
  publishOutcome,
  relativeTime,
  releaseView,
  shortFingerprint,
  signerSummary,
  signerWarnings,
  validityLabel,
  type Pill,
  type PublishResponse,
  type SignerReport,
  type SigningKey,
  type SigningKeysReport,
} from "./platform-signer.js";

async function platformApi<T>(path: string): Promise<T> {
  const response = await fetch(path, { credentials: "include", headers: { accept: "application/json" } });
  const body = await response.json().catch(() => undefined) as unknown;
  return platformResponse<T>(response.status, body);
}

function Brand() {
  return <a className="pr-brand" href="/"><img src="/sectigo-logo.svg" alt="Sectigo" /><span>EDGE</span></a>;
}

function PillBadge({ pill }: { pill: Pill }) {
  return <span className={`ps-pill ps-pill--${pill.tone}`}>{pill.label}</span>;
}

function yesNo(value: boolean | null | undefined, yes = "Yes", no = "No"): Pill {
  if (value === true) return { tone: "ok", label: yes };
  if (value === false) return { tone: "critical", label: no };
  return { tone: "muted", label: "Unknown" };
}

function CopyFingerprint({ value }: { value: string | null }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="ps-muted">—</span>;
  return <button type="button" className="ps-copy" title={`SHA-256 ${value}`} onClick={() => {
    void navigator.clipboard?.writeText(value).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }).catch(() => undefined);
  }}><code>{shortFingerprint(value)}</code>{copied ? <Check /> : <Copy />}<span className="ps-sr">{copied ? "Copied" : "Copy full fingerprint"}</span></button>;
}

function SignerCard({ report }: { report: SignerReport }) {
  const summary = signerSummary(report);
  const warnings = signerWarnings(report.warnings);
  const registry = report.status?.registry;
  const now = new Date(report.checked_at);
  return <section className="ps-card">
    <header><div className="pr-record-icon"><Server /></div><div><small>PROTECTED SIGNER</small><h2>Remote signer</h2></div><PillBadge pill={summary} /></header>
    {warnings.length > 0 && <ul className="ps-warnings" role="alert">
      {warnings.map((warning) => <li key={warning.code} className={`ps-warning ps-warning--${warning.severity}`}>{warning.severity === "critical" ? <ShieldAlert /> : <AlertTriangle />}<div><strong>{warning.title}</strong><span>{warning.detail}</span></div></li>)}
    </ul>}
    <dl className="ps-grid">
      <div><dt>Configured</dt><dd><PillBadge pill={yesNo(report.configured)} /><span>Binding {report.configuration.signer_binding ? "✓" : "✗"} · URL {report.configuration.signer_url ? "✓" : "✗"} · token {report.configuration.service_token ? "✓" : "✗"}</span></dd></div>
      <div><dt>Reachable</dt><dd><PillBadge pill={yesNo(report.reachable)} /><span>{report.latency_ms === null ? "No response" : `${report.latency_ms} ms health round-trip`}</span></dd></div>
      <div><dt>Transport</dt><dd>{report.status?.transport ?? "—"}<span>Idempotency: {report.status?.idempotency ?? "—"}</span></dd></div>
      <div><dt>Receipt key</dt><dd><PillBadge pill={report.receipt_key_id_matches === null ? { tone: "muted", label: "Not checked" } : yesNo(report.receipt_key_id_matches, "Matches edge", "Mismatch")} /><span><code>{report.status?.receipt_key_id ?? report.health?.receipt_key_id ?? "—"}</code></span></dd></div>
      <div><dt>Tenant registry</dt><dd>{registry ? <PillBadge pill={yesNo(registry.loaded, "Loaded", "Not loaded")} /> : "—"}<span>{registry?.tenants !== null && registry?.tenants !== undefined ? `${registry.tenants} tenants · ` : ""}issued {relativeTime(registry?.issued_at, now)}</span></dd></div>
      <div><dt>Registry expires</dt><dd>{registry?.expires_at ? relativeTime(registry.expires_at, now) : "—"}<span>{registry?.expires_at ? new Date(registry.expires_at).toLocaleString() : "No expiry reported"}</span></dd></div>
      <div className="ps-wide"><dt>Providers</dt><dd className="ps-tags">{report.status?.providers.length ? report.status.providers.map((provider) => <code key={provider}>{provider}</code>) : <span>None reported</span>}</dd></div>
      {registry?.payload_sha256 && <div className="ps-wide"><dt>Registry payload SHA-256</dt><dd><CopyFingerprint value={registry.payload_sha256} /></dd></div>}
    </dl>
    {report.status_error && <p className="ps-footnote">Status call failed: <code>{report.status_error}</code></p>}
    <p className="ps-footnote">Checked {new Date(report.checked_at).toLocaleString()}</p>
  </section>;
}

function KeysTable({ report }: { report: SigningKeysReport }) {
  return <section className="ps-card">
    <header><div className="pr-record-icon"><KeyRound /></div><div><small>EDGE SIGNING KEYS</small><h2>Key sets</h2></div></header>
    <p className="ps-boundary"><LockKeyhole /> Only public keys and fingerprints are shown. Private keys never leave the edge or the signer, and this page cannot display them.</p>
    <div className="ps-table-wrap"><table className="ps-table">
      <thead><tr><th>Purpose</th><th>Key ID</th><th>Fingerprint (SHA-256)</th><th>Validity</th><th>Status</th><th>Private key</th><th>Pair check</th></tr></thead>
      <tbody>{report.keys.map((key: SigningKey) => {
        const release = releaseView(key.published_in_release, report.release_version);
        return <tr key={key.purpose}>
          <td data-label="Purpose"><strong>{key.label}</strong><span>{key.purpose.replaceAll("_", " ")}</span></td>
          <td data-label="Key ID"><code>{key.key_id ?? "—"}</code>{release && <PillBadge pill={release} />}</td>
          <td data-label="Fingerprint"><CopyFingerprint value={key.fingerprint_sha256} />{key.public_key_source === "derived_from_private" && <span>derived from private key</span>}</td>
          <td data-label="Validity">{validityLabel(key)}</td>
          <td data-label="Status"><PillBadge pill={keyStatusPill(key)} />{key.problems.length > 0 && <span>{key.problems.map((problem) => problem.replaceAll("_", " ")).join(", ")}</span>}</td>
          <td data-label="Private key">{key.private_key_configured === "n/a" ? <PillBadge pill={{ tone: "muted", label: "In signer" }} /> : <PillBadge pill={yesNo(key.private_key_configured, "Configured", "Missing")} />}</td>
          <td data-label="Pair check"><PillBadge pill={pairCheckView(key)} /></td>
        </tr>;
      })}</tbody>
    </table></div>
    <p className="ps-footnote">Release trust file: {report.release_trust_url ? <a href={report.release_trust_url} target="_blank" rel="noreferrer">{report.release_version}/bootstrap-trust.json</a> : <code>{report.release_version}</code>}</p>
  </section>;
}

async function publishRegistry(): Promise<PublishResponse> {
  const response = await fetch("/api/v1/platform/signer/registry/publish", {
    method: "POST",
    credentials: "include",
    headers: { accept: "application/json", "content-type": "application/json" },
    body: "{}",
  });
  const body = await response.json().catch(() => undefined) as unknown;
  return platformResponse<PublishResponse>(response.status, body);
}

export function PlatformSignerPage() {
  const [signer, setSigner] = useState<SignerReport>();
  const [keys, setKeys] = useState<SigningKeysReport>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reauth, setReauth] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [outcome, setOutcome] = useState<ReturnType<typeof publishOutcome>>();

  const load = useCallback(async () => {
    setLoading(true); setError(""); setReauth(false);
    const [signerResult, keysResult] = await Promise.allSettled([
      platformApi<SignerReport>("/api/v1/platform/signer"),
      platformApi<SigningKeysReport>("/api/v1/platform/signing-keys"),
    ]);
    if (signerResult.status === "fulfilled") setSigner(signerResult.value);
    if (keysResult.status === "fulfilled") setKeys(keysResult.value);
    const failure = [signerResult, keysResult].find((result): result is PromiseRejectedResult => result.status === "rejected");
    if (failure) {
      setError(failure.reason instanceof Error ? failure.reason.message : "Platform status could not be loaded");
      setReauth(needsReauthentication(failure.reason));
    }
    setLoading(false);
  }, []);
  useEffect(() => { void load(); }, [load]);

  const publish = async () => {
    setPublishing(true); setError(""); setReauth(false); setOutcome(undefined);
    try {
      setOutcome(publishOutcome(await publishRegistry()));
      await load();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "The registry could not be published");
      setReauth(needsReauthentication(failure));
    } finally {
      setPublishing(false);
    }
  };

  return <div className="pr-page">
    <header className="pr-top"><Brand /><div><span><LockKeyhole /> PLATFORM-RESTRICTED</span><a href="/console">Tenant console <ArrowRight /></a></div></header>
    <PlatformNav active="signer" />
    <main className="pr-shell">
      <section className="pr-heading"><div><small>SECTIGO EDGE · PLATFORM TRUST</small><h1>Signer & keys</h1><p>Live health of the protected signer and the public half of every key set this edge signs with. Publishing re-signs the registry from live tenant state, exactly as the hourly schedule does.</p></div><aside><ShieldAlert /><span>Authority boundary</span><strong>Private keys are never returned by the API</strong></aside></section>
      <section className="pr-controls"><span className="ps-controls-label">Live from this edge · registry re-signed hourly</span><div className="ps-actions"><button onClick={() => void publish()} disabled={publishing || loading}><Upload className={publishing ? "spin" : ""} /> Publish registry now</button><button onClick={() => void load()} disabled={loading}><RefreshCw className={loading ? "spin" : ""} /> Refresh</button></div></section>
      {outcome && <div className={`ps-outcome ps-outcome-${outcome.tone}`} role="status"><strong>{outcome.title}</strong>{outcome.details.length > 0 && <ul>{outcome.details.map((detail) => <li key={detail}>{detail}</li>)}</ul>}</div>}
      {error && <div className="pr-error" role="alert">{error}{reauth && <button type="button" className="pr-step-up" onClick={() => void reauthenticateWithAccess()}><RefreshCw /> Re-authenticate</button>}</div>}
      <div className="ps-stack">
        {signer ? <SignerCard report={signer} /> : loading && <div className="pr-empty"><RefreshCw className="spin" /><h2>Checking signer…</h2></div>}
        {keys ? <KeysTable report={keys} /> : loading && <div className="pr-empty"><RefreshCw className="spin" /><h2>Inspecting key sets…</h2></div>}
      </div>
    </main>
  </div>;
}
